
import React, { useEffect, useState } from "react";
import { Chart } from "@/components/ui/chart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartBar, Clock, DollarSign, ShoppingCart } from "lucide-react";
import {
  Customer,
  getAllCustomers,
  PurchaseTime,
  PurchaseChannel
} from "@/lib/db/customerDb";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PurchaseBehaviorTab = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timePreferenceData, setTimePreferenceData] = useState<any[]>([]);
  const [channelPreferenceData, setChannelPreferenceData] = useState<any[]>([]);
  const [averageOrderData, setAverageOrderData] = useState<any[]>([]);
  const [timeFilter, setTimeFilter] = useState<string>("all");
  const { toast } = useToast();

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setIsLoading(true);
        const data = await getAllCustomers();
        setCustomers(data);
        processChartData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load customer data", error);
        toast({
          variant: "destructive",
          title: "Error loading data",
          description: "Could not load customer data for analysis.",
        });
        setIsLoading(false);
      }
    };
    
    loadCustomers();
  }, [toast]);

  const processChartData = (customerData: Customer[]) => {
    // Process time preference data
    const timePreferences: Record<PurchaseTime, number> = {
      'Morning': 0,
      'Afternoon': 0,
      'Evening': 0
    };
    
    customerData.forEach(customer => {
      timePreferences[customer.preferredPurchaseTime]++;
    });
    
    const timeData = Object.entries(timePreferences).map(([time, count]) => ({
      name: time,
      value: count
    }));
    
    setTimePreferenceData(timeData);
    
    // Process channel preference data
    const channelPreferences: Record<PurchaseChannel, number> = {
      'POS': 0,
      'Jimatshop': 0
    };
    
    customerData.forEach(customer => {
      channelPreferences[customer.preferredPurchaseChannel]++;
    });
    
    const channelData = Object.entries(channelPreferences).map(([channel, count]) => ({
      name: channel,
      value: count
    }));
    
    setChannelPreferenceData(channelData);
    
    // Process average order values by loyalty tier
    const loyaltyTierOrders: Record<string, { total: number, count: number }> = {};
    
    customerData.forEach(customer => {
      if (!loyaltyTierOrders[customer.loyaltyTier]) {
        loyaltyTierOrders[customer.loyaltyTier] = {
          total: 0,
          count: 0
        };
      }
      
      loyaltyTierOrders[customer.loyaltyTier].total += customer.averageOrderValue;
      loyaltyTierOrders[customer.loyaltyTier].count++;
    });
    
    const orderData = Object.entries(loyaltyTierOrders).map(([tier, data]) => ({
      name: tier,
      value: data.total / data.count
    }));
    
    setAverageOrderData(orderData);
  };

  const filterCustomersByTime = () => {
    if (timeFilter === "all") {
      return customers;
    }
    return customers.filter(customer => customer.preferredPurchaseTime === timeFilter);
  };

  const calculateAverageOrderValue = () => {
    const filteredCustomers = filterCustomersByTime();
    if (filteredCustomers.length === 0) return 0;
    
    const sum = filteredCustomers.reduce((acc, customer) => acc + customer.averageOrderValue, 0);
    return sum / filteredCustomers.length;
  };

  const calculateTotalSpent = () => {
    const filteredCustomers = filterCustomersByTime();
    return filteredCustomers.reduce((acc, customer) => acc + customer.totalSpent, 0);
  };

  const calculateAverageVisits = () => {
    const filteredCustomers = filterCustomersByTime();
    if (filteredCustomers.length === 0) return 0;
    
    const sum = filteredCustomers.reduce((acc, customer) => acc + customer.visitFrequency, 0);
    return (sum / filteredCustomers.length).toFixed(1);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChartBar className="text-muted-foreground h-5 w-5" />
          <h2 className="text-xl font-semibold">Purchase Behavior Analysis</h2>
        </div>
        
        <Select value={timeFilter} onValueChange={setTimeFilter}>
          <SelectTrigger className="w-[180px]">
            <Clock className="h-4 w-4 mr-2" />
            <span>{timeFilter === "all" ? "All Time Periods" : timeFilter}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time Periods</SelectItem>
            <SelectItem value="Morning">Morning</SelectItem>
            <SelectItem value="Afternoon">Afternoon</SelectItem>
            <SelectItem value="Evening">Evening</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Key Metrics Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Average Order Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 text-muted-foreground mr-2" />
              <div className="text-2xl font-bold">
                {new Intl.NumberFormat('en-MY', {
                  style: 'currency',
                  currency: 'MYR',
                  minimumFractionDigits: 2
                }).format(calculateAverageOrderValue())}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Customer Spending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 text-muted-foreground mr-2" />
              <div className="text-2xl font-bold">
                {new Intl.NumberFormat('en-MY', {
                  style: 'currency',
                  currency: 'MYR',
                  maximumFractionDigits: 0
                }).format(calculateTotalSpent())}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Average Monthly Visits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <ShoppingCart className="h-5 w-5 text-muted-foreground mr-2" />
              <div className="text-2xl font-bold">{calculateAverageVisits()}</div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Preferred Purchase Time</CardTitle>
            <CardDescription>Distribution of customers by preferred shopping time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Chart
                type="pie"
                data={timePreferenceData}
                index="name"
                categories={["value"]}
                colors={["#8B5CF6", "#EC4899", "#F97316"]}
                valueFormatter={(value) => `${value} customers`}
              />
            </div>
          </CardContent>
        </Card>
        
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Preferred Purchase Channel</CardTitle>
            <CardDescription>Distribution of customers by purchase channel</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Chart
                type="pie"
                data={channelPreferenceData}
                index="name"
                categories={["value"]}
                colors={["#2563EB", "#10B981"]}
                valueFormatter={(value) => `${value} customers`}
              />
            </div>
          </CardContent>
        </Card>
        
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <CardTitle>Average Order Value by Loyalty Tier</CardTitle>
            <CardDescription>How different customer tiers spend per order</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Chart
                type="bar"
                data={averageOrderData}
                index="name"
                categories={["value"]}
                colors={["#8B5CF6"]}
                valueFormatter={(value) => `RM ${value.toFixed(2)}`}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchaseBehaviorTab;
